import { useState, useEffect } from 'react';

/**
 * Detects whether the current device should use reduced animations.
 *
 * Returns true when ANY of the following is true:
 *  - The user has enabled `prefers-reduced-motion: reduce` in their OS.
 *  - The viewport is narrow (mobile) AND the device has limited CPU
 *    (hardwareConcurrency <= 4 or deviceMemory <= 4 GB).
 *
 * Components use this to disable blurred orbs, marquees, and other
 * GPU-intensive decorative animations.
 */
const useLowPower = () => {
  const [lowPower, setLowPower] = useState(() => {
    if (typeof window === 'undefined') return false;
    const mq = typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;
    if (mq?.matches) return true;
    if (window.innerWidth >= 768) return false;
    const cores = typeof navigator !== 'undefined' ? navigator.hardwareConcurrency || 8 : 8;
    const mem = typeof navigator !== 'undefined' ? navigator.deviceMemory || 8 : 8;
    return cores <= 4 || mem <= 4;
  });

  useEffect(() => {
    const mq = typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;

    const check = () => {
      // Always respect explicit reduced-motion preference
      if (mq?.matches) {
        setLowPower(true);
        return;
      }

      // On mobile-width screens, check CPU/memory heuristics
      const isMobile = window.innerWidth < 768;
      if (!isMobile) {
        setLowPower(false);
        return;
      }

      const cores = navigator.hardwareConcurrency || 8;
      const mem = navigator.deviceMemory || 8; // GB, Chrome only
      setLowPower(cores <= 4 || mem <= 4);
    };

    check();
    if (mq?.addEventListener) mq.addEventListener('change', check);
    else mq?.addListener?.(check);
    window.addEventListener('resize', check, { passive: true });
    return () => {
      if (mq?.removeEventListener) mq.removeEventListener('change', check);
      else mq?.removeListener?.(check);
      window.removeEventListener('resize', check);
    };
  }, []);

  return lowPower;
};

export default useLowPower;
